import type {
  DependentCount,
  DooColour,
  DooColourInsert,
  DooDoorStyle,
  DooDoorStyleInsert,
  DooDoorStyleSupplierMapping,
  DooGlass,
  DooGlassInsert,
  DooHardware,
  DooHardwareCollection,
  DooHardwareCollectionInsert,
  DooHardwareFinish,
  DooHardwareFinishInsert,
  DooHardwareInsert,
  DooHardwareType,
  DooHardwareTypeInsert,
  DooMappingInsert,
  DooMaterial,
  DooMaterialInsert,
  DooSupplier,
  DooSupplierInsert,
  DooTableName,
} from '../types';
import { dooTable } from './client';
import { throwOnError } from './errors';
import { omitManagedRecordFields } from './recordMeta';

async function selectAll<T>(table: DooTableName, orderBy: string): Promise<T[]> {
  const result = await dooTable(table).select('*').order(orderBy, { ascending: true });
  return (throwOnError(result, `Failed to load ${table}`) as T[]) ?? [];
}

async function insertReturning<T>(table: DooTableName, row: object): Promise<T> {
  const result = await dooTable(table)
    .insert(omitManagedRecordFields(row) as never)
    .select('*')
    .single();
  return throwOnError(result, `Failed to create ${table}`) as unknown as T;
}

export async function findByColumn<T>(
  table: DooTableName,
  column: string,
  value: string
): Promise<T | null> {
  const result = await dooTable(table).select('*').eq(column, value).maybeSingle();
  if (result.error) {
    throwOnError(result, `Failed to find ${table}`);
  }
  return (result.data as T | null) ?? null;
}

async function updateReturning<T>(
  table: DooTableName,
  pk: string,
  id: string,
  patch: object
): Promise<T> {
  const result = await dooTable(table)
    .update(omitManagedRecordFields(patch) as never)
    .eq(pk, id)
    .select('*')
    .single();
  return throwOnError(result, `Failed to update ${table}`) as unknown as T;
}

async function deleteByPk(table: DooTableName, pk: string, id: string): Promise<void> {
  const result = await dooTable(table).delete().eq(pk, id);
  throwOnError(result, `Failed to delete ${table}`);
}

async function countEq(table: DooTableName, column: string, id: string): Promise<number> {
  const result = await dooTable(table).select('*', { count: 'exact', head: true }).eq(column, id);
  if (result.error) {
    throwOnError(result, `Failed to check ${table}`);
  }
  return result.count ?? 0;
}

function nonemptyDependents(items: DependentCount[]): DependentCount[] {
  return items.filter((item) => item.count > 0);
}

export async function listMaterials(): Promise<DooMaterial[]> {
  return selectAll<DooMaterial>('doo_materials', 'material_name');
}

export async function createMaterial(row: DooMaterialInsert): Promise<DooMaterial> {
  return insertReturning('doo_materials', row);
}

export async function updateMaterial(
  id: string,
  patch: Partial<DooMaterialInsert>
): Promise<DooMaterial> {
  return updateReturning('doo_materials', 'material_id', id, patch);
}

export async function materialDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    { label: 'door styles', count: await countEq('doo_door_styles', 'material_id', id) },
  ]);
}

export async function deleteMaterial(id: string): Promise<void> {
  const dependents = await materialDependents(id);
  if (dependents.length) {
    throw new Error(`Cannot delete this material while it is used by ${formatDependents(dependents)}.`);
  }
  return deleteByPk('doo_materials', 'material_id', id);
}

export async function listSuppliers(): Promise<DooSupplier[]> {
  return selectAll<DooSupplier>('doo_suppliers', 'supplier_name');
}

export async function createSupplier(row: DooSupplierInsert): Promise<DooSupplier> {
  return insertReturning('doo_suppliers', row);
}

export async function updateSupplier(
  id: string,
  patch: Partial<DooSupplierInsert>
): Promise<DooSupplier> {
  return updateReturning('doo_suppliers', 'supplier_id', id, patch);
}

export async function supplierDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    {
      label: 'style mappings',
      count: await countEq('doo_door_style_supplier_mappings', 'supplier_id', id),
    },
    { label: 'glass products', count: await countEq('doo_glass', 'supplier_id', id) },
  ]);
}

export async function deleteSupplier(id: string): Promise<void> {
  const dependents = await supplierDependents(id);
  if (dependents.length) {
    throw new Error(`Cannot delete this supplier while it is used by ${formatDependents(dependents)}.`);
  }
  return deleteByPk('doo_suppliers', 'supplier_id', id);
}

export async function listColours(): Promise<DooColour[]> {
  return selectAll<DooColour>('doo_colours', 'colour_name');
}

export async function createColour(row: DooColourInsert): Promise<DooColour> {
  return insertReturning('doo_colours', row);
}

export async function updateColour(id: string, patch: Partial<DooColourInsert>): Promise<DooColour> {
  return updateReturning('doo_colours', 'colour_id', id, patch);
}

export async function colourDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    { label: 'door-style colours', count: await countEq('doo_door_style_colours', 'colour_id', id) },
  ]);
}

export async function listHardwareFinishes(): Promise<DooHardwareFinish[]> {
  return selectAll<DooHardwareFinish>('doo_hardware_finishes', 'finish_name');
}

export async function createHardwareFinish(row: DooHardwareFinishInsert): Promise<DooHardwareFinish> {
  return insertReturning('doo_hardware_finishes', row);
}

export async function updateHardwareFinish(
  id: string,
  patch: Partial<DooHardwareFinishInsert>
): Promise<DooHardwareFinish> {
  return updateReturning('doo_hardware_finishes', 'finish_id', id, patch);
}

export async function finishDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    { label: 'hardware products', count: await countEq('doo_hardware_colours', 'finish_id', id) },
  ]);
}

export async function deleteHardwareFinish(id: string): Promise<void> {
  const dependents = await finishDependents(id);
  if (dependents.length) {
    throw new Error(
      `Cannot delete this hardware finish while it is used by ${formatDependents(dependents)}. Deprecate or divest it instead.`
    );
  }
  return deleteByPk('doo_hardware_finishes', 'finish_id', id);
}

export async function deleteColour(id: string): Promise<void> {
  const dependents = await colourDependents(id);
  if (dependents.length) {
    throw new Error(`Cannot delete this colour while it is used by ${formatDependents(dependents)}. Deprecate or divest it instead.`);
  }
  return deleteByPk('doo_colours', 'colour_id', id);
}

export async function listDoorStyles(): Promise<DooDoorStyle[]> {
  return selectAll<DooDoorStyle>('doo_door_styles', 'keystone_style_name');
}

export async function getDoorStyle(id: string): Promise<DooDoorStyle> {
  const result = await dooTable('doo_door_styles').select('*').eq('door_style_id', id).single();
  return throwOnError(result, 'Failed to load door style') as unknown as DooDoorStyle;
}

export async function createDoorStyle(row: DooDoorStyleInsert): Promise<DooDoorStyle> {
  return insertReturning('doo_door_styles', row);
}

export async function updateDoorStyle(
  id: string,
  patch: Partial<DooDoorStyleInsert>
): Promise<DooDoorStyle> {
  return updateReturning('doo_door_styles', 'door_style_id', id, patch);
}

async function countJunctionViaMappings(
  junctionTable: DooTableName,
  doorStyleId: string
): Promise<number> {
  const mappings = await listStyleMappings(doorStyleId);
  if (!mappings.length) return 0;
  const mappingIds = mappings.map((row) => row.mapping_id);
  const result = await dooTable(junctionTable)
    .select('*', { count: 'exact', head: true })
    .in('mapping_id', mappingIds);
  if (result.error) {
    throwOnError(result, `Failed to check ${junctionTable}`);
  }
  return result.count ?? 0;
}

export async function doorStyleDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    {
      label: 'supplier mappings',
      count: await countEq('doo_door_style_supplier_mappings', 'door_style_id', id),
    },
    {
      label: 'colour links',
      count: await countJunctionViaMappings('doo_door_style_colours', id),
    },
    {
      label: 'glass links',
      count: await countJunctionViaMappings('doo_door_style_glass', id),
    },
    {
      label: 'hardware links',
      count: await countJunctionViaMappings('doo_door_style_hardware', id),
    },
  ]);
}

export async function mappingDependents(mappingId: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    { label: 'colour links', count: await countEq('doo_door_style_colours', 'mapping_id', mappingId) },
    { label: 'glass links', count: await countEq('doo_door_style_glass', 'mapping_id', mappingId) },
    {
      label: 'hardware links',
      count: await countEq('doo_door_style_hardware', 'mapping_id', mappingId),
    },
  ]);
}

export async function deleteDoorStyle(id: string): Promise<void> {
  const dependents = await doorStyleDependents(id);
  if (dependents.length) {
    throw new Error(
      `Cannot delete this door style while it still has ${formatDependents(dependents)}. Deprecate or divest it instead.`
    );
  }
  return deleteByPk('doo_door_styles', 'door_style_id', id);
}

export async function listStyleMappings(doorStyleId: string): Promise<DooDoorStyleSupplierMapping[]> {
  const result = await dooTable('doo_door_style_supplier_mappings')
    .select('*')
    .eq('door_style_id', doorStyleId)
    .order('created_at', { ascending: true });
  return (throwOnError(result, 'Failed to load supplier mappings') as DooDoorStyleSupplierMapping[]) ?? [];
}

export async function listAllStyleMappings(): Promise<DooDoorStyleSupplierMapping[]> {
  return selectAll<DooDoorStyleSupplierMapping>('doo_door_style_supplier_mappings', 'created_at');
}

export async function createStyleMapping(
  row: DooMappingInsert
): Promise<DooDoorStyleSupplierMapping> {
  return insertReturning('doo_door_style_supplier_mappings', row);
}

export async function updateStyleMapping(
  id: string,
  patch: Partial<DooMappingInsert>
): Promise<DooDoorStyleSupplierMapping> {
  return updateReturning('doo_door_style_supplier_mappings', 'mapping_id', id, patch);
}

export async function deleteStyleMapping(id: string): Promise<void> {
  return deleteByPk('doo_door_style_supplier_mappings', 'mapping_id', id);
}

export async function listHardwareTypes(): Promise<DooHardwareType[]> {
  return selectAll<DooHardwareType>('doo_hardware_types', 'hardware_type_name');
}

export async function createHardwareType(row: DooHardwareTypeInsert): Promise<DooHardwareType> {
  return insertReturning('doo_hardware_types', row);
}

export async function updateHardwareType(
  id: string,
  patch: Partial<DooHardwareTypeInsert>
): Promise<DooHardwareType> {
  return updateReturning('doo_hardware_types', 'hardware_type_id', id, patch);
}

export async function hardwareTypeDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    { label: 'hardware products', count: await countEq('doo_hardware', 'hardware_type_id', id) },
  ]);
}

export async function deleteHardwareType(id: string): Promise<void> {
  const dependents = await hardwareTypeDependents(id);
  if (dependents.length) {
    throw new Error(`Cannot delete this type while it is used by ${formatDependents(dependents)}.`);
  }
  return deleteByPk('doo_hardware_types', 'hardware_type_id', id);
}

export async function listHardwareCollections(): Promise<DooHardwareCollection[]> {
  return selectAll<DooHardwareCollection>('doo_hardware_collections', 'collection_name');
}

export async function createHardwareCollection(
  row: DooHardwareCollectionInsert
): Promise<DooHardwareCollection> {
  return insertReturning('doo_hardware_collections', row);
}

export async function updateHardwareCollection(
  id: string,
  patch: Partial<DooHardwareCollectionInsert>
): Promise<DooHardwareCollection> {
  return updateReturning('doo_hardware_collections', 'collection_id', id, patch);
}

export async function hardwareCollectionDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    { label: 'hardware products', count: await countEq('doo_hardware', 'collection_id', id) },
  ]);
}

export async function deleteHardwareCollection(id: string): Promise<void> {
  const dependents = await hardwareCollectionDependents(id);
  if (dependents.length) {
    throw new Error(
      `Cannot delete this collection while it is used by ${formatDependents(dependents)}. Deprecate or divest it instead.`
    );
  }
  return deleteByPk('doo_hardware_collections', 'collection_id', id);
}

export async function listHardware(): Promise<DooHardware[]> {
  return selectAll<DooHardware>('doo_hardware', 'hardware_name');
}

export async function createHardware(row: DooHardwareInsert): Promise<DooHardware> {
  return insertReturning('doo_hardware', row);
}

export async function updateHardware(
  id: string,
  patch: Partial<DooHardwareInsert>
): Promise<DooHardware> {
  return updateReturning('doo_hardware', 'hardware_id', id, patch);
}

export async function hardwareDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    { label: 'finish links', count: await countEq('doo_hardware_colours', 'hardware_id', id) },
    { label: 'door-style hardware links', count: await countEq('doo_door_style_hardware', 'hardware_id', id) },
  ]);
}

export async function deleteHardware(id: string): Promise<void> {
  const dependents = await hardwareDependents(id);
  if (dependents.length) {
    throw new Error(
      `Cannot delete this hardware product while it is used by ${formatDependents(dependents)}. Deprecate or divest it instead.`
    );
  }
  return deleteByPk('doo_hardware', 'hardware_id', id);
}

export async function listHardwareFinishIds(hardwareId: string): Promise<string[]> {
  const result = await dooTable('doo_hardware_colours')
    .select('finish_id')
    .eq('hardware_id', hardwareId);
  const rows = (throwOnError(result, 'Failed to load hardware finishes') as { finish_id: string }[]) ?? [];
  return rows.map((row) => row.finish_id);
}

/** @deprecated Use listHardwareFinishIds */
export async function listHardwareColourIds(hardwareId: string): Promise<string[]> {
  return listHardwareFinishIds(hardwareId);
}

export async function listGlass(): Promise<DooGlass[]> {
  return selectAll<DooGlass>('doo_glass', 'glass_name');
}

export async function createGlass(row: DooGlassInsert): Promise<DooGlass> {
  return insertReturning('doo_glass', row);
}

export async function updateGlass(id: string, patch: Partial<DooGlassInsert>): Promise<DooGlass> {
  return updateReturning('doo_glass', 'glass_id', id, patch);
}

export async function glassDependents(id: string): Promise<DependentCount[]> {
  return nonemptyDependents([
    { label: 'door-style glass links', count: await countEq('doo_door_style_glass', 'glass_id', id) },
  ]);
}

export async function deleteGlass(id: string): Promise<void> {
  const dependents = await glassDependents(id);
  if (dependents.length) {
    throw new Error(
      `Cannot delete this glass while it is used by ${formatDependents(dependents)}. Deprecate or divest it instead.`
    );
  }
  return deleteByPk('doo_glass', 'glass_id', id);
}

export function formatDependents(dependents: DependentCount[]): string {
  return dependents.map((item) => `${item.count} ${item.label}`).join(', ');
}
